import React from 'react';
import {observer} from 'mobx-react';

import {MOBILE_MAX_SCREEN_SIZE} from 'base/search.js';
import autobind from 'common/decorators/autobind.js';
import {
    PRAC_LOC_TYPE,
    NO_RESULT_WITH_SKINNED_TEXT,
    RESULT_WITH_SKINNED_TEXT,
} from 'core/constants.js';
import {DirectoryStoreContextV2} from 'core/stores/RootStore.js';
import ButtonV2 from 'core/components/v2/ButtonV2.js';
import PracticeLocationV2 from 'directory/v2/components/PracticeLocationV2.js';
import ProfileCardV2 from 'directory/v2/components/ProfileCardV2.js';
import SkeletonCardListV2 from 'directory/v2/components/SkeletonCardListV2.js';
import SkeletonCardV2 from 'directory/v2/components/SkeletonCardV2.js';
import NoResultsNotice from 'directory/components/NoResultsNotice.js';
import ResultsSummaryV2 from 'directory/v2/components/ResultsSummaryV2.js';
import {NotificationV2} from '@healthshareau/hs-component-library';

export default
@observer
class SearchResultsV2 extends React.Component {
    static contextType = DirectoryStoreContextV2;

    isMobile() {
        return window.matchMedia(`(max-width: ${MOBILE_MAX_SCREEN_SIZE})`)
            .matches;
    }

    @autobind
    loadMore() {
        const {locationSearchAPI} = this.context;
        if (locationSearchAPI.isLoading || !locationSearchAPI.next) {
            return;
        }
        locationSearchAPI.loadMoreResults();
        this.updatePageParam(locationSearchAPI.page);
    }

    @autobind
    async loadPrevious() {
        const {locationSearchAPI} = this.context;
        if (locationSearchAPI.isLoading || !locationSearchAPI.hasPrevious) {
            return;
        }
        const scroller = document.scrollingElement || document.documentElement;
        const previousScrollHeight = scroller.scrollHeight;
        const previousScrollTop = scroller.scrollTop;

        await locationSearchAPI.loadPreviousResults();

        this.updatePageParam(locationSearchAPI.startPage);
        const reachedPageOne = locationSearchAPI.startPage === 1;

        window.requestAnimationFrame(() => {
            if (reachedPageOne) {
                scroller.scrollTop = 0;
                return;
            }
            const addedHeight = scroller.scrollHeight - previousScrollHeight;
            scroller.scrollTop = previousScrollTop + addedHeight;
        });
    }

    // Builds a path + query string for the directory page with `?page=<page>`,
    // preserving every other existing query param. Page 1 is the clean url
    // with no `page` param.
    pageURL(page) {
        const url = new URL(window.location.href);
        if (page > 1) {
            url.searchParams.set('page', page);
        } else {
            url.searchParams.delete('page');
        }
        return `${url.pathname}${url.search}`;
    }

    updatePageParam(page) {
        window.history.pushState(
            window.history.state,
            '',
            this.pageURL(page),
        );
    }

    @autobind
    turnOffOnlineBookingsFilter() {
        this.context.updateStore({
            filterByOnlineBookings: false,
        });
    }

    renderResult(pracProps) {
        if (pracProps.type === PRAC_LOC_TYPE) {
            return <PracticeLocationV2 {...pracProps} />;
        } else {
            return <ProfileCardV2 {...pracProps} />;
        }
    }

    renderDistanceHeader(index) {
        const {
            specialty,
            rootStore: {
                paramStore: {isReferrals, isProUser, hasSkin},
                healthFundStore: {healthFundThemeColor},
            },
        } = this.context;

        const specialtyName =
            isReferrals || isProUser
                ? specialty.pluralProfessionalName
                : specialty.pluralName;
        let heading = `More ${specialtyName}`;
        let body = `These ${specialtyName} are more than 150km away.`;

        if (index === 0) {
            heading = `There are no ${specialtyName} close to your search location`;
            body = `These ${specialtyName} are more than 150km away, but may still be the closest to you.`;
        }

        return (
            <NotificationV2
                colorTheme={
                    hasSkin && healthFundThemeColor
                        ? healthFundThemeColor
                        : null
                }
                customClass="location"
                customIcon="markerPinV2"
                htmlText={
                    '<strong>More practitioners</strong> These practices are more than 150km away.'
                }
                type="success"
            />
        );
    }

    renderResults() {
        const {
            bannerAdsHaveLoaded,
            bannerAdsMidSessionFetch,
            interest,
            results,
            specialty,
            telehealthDirectory,
            rootStore: {
                healthFundStore: {healthFund},
                paramStore: {isReferrals},
            },
        } = this.context;

        const distanceHeaderPosition = results.findIndex(
            (prac) => prac.distance > 150,
        );

        if (bannerAdsHaveLoaded || bannerAdsMidSessionFetch) {
            return results?.map((practitioner, i) => {
                const pracProps = {
                    ...practitioner,
                    healthFund,
                    isReferrals,
                    specialty,
                    interest,
                };

                return (
                    <React.Fragment key={practitioner.id}>
                        {distanceHeaderPosition === i &&
                            !telehealthDirectory &&
                            this.renderDistanceHeader(i)}
                        {this.renderResult(pracProps)}
                    </React.Fragment>
                );
            });
        }
    }

    renderLoadPrevious() {
        const {locationSearchAPI} = this.context;
        if (
            !locationSearchAPI.hasPrevious ||
            !locationSearchAPI.results?.length
        ) {
            return null;
        }
        return (
            <div className="load-previous">
                <ButtonV2
                    action={this.loadPrevious}
                    customClass="load-previous-button"
                    disabled={locationSearchAPI.isLoadingPrevious}
                    text="Load previous"
                />
                <a
                    className="load-previous-prev-link"
                    href={this.pageURL(locationSearchAPI.startPage - 1)}
                />
            </div>
        );
    }

    renderLoadMoreButton() {
        const {locationSearchAPI} = this.context;
        if (!locationSearchAPI.results?.length) {
            return null;
        }
        return (
            <div className="load-more">
                <ResultsSummaryV2 />
                {locationSearchAPI.next && (
                    <React.Fragment>
                        <ButtonV2
                            action={this.loadMore}
                            customClass="load-more-button"
                            disabled={locationSearchAPI.isLoading}
                            text="Load more"
                        />
                        <a
                            className="load-more-next-link"
                            href={this.pageURL(locationSearchAPI.page + 1)}
                        />
                    </React.Fragment>
                )}
            </div>
        );
    }

    render() {
        const {
            bannerAdsHaveLoaded,
            bannerAdsMidSessionFetch,
            bannerAds,
            locationSearchAPI,
            results,
            resultsReady,
            searchResultsError,
            filterByOnlineBookings,
            rootStore: {
                paramStore: {hasSkin},
            },
        } = this.context;

        const NoResultText = hasSkin
            ? RESULT_WITH_SKINNED_TEXT
            : NO_RESULT_WITH_SKINNED_TEXT;

        if (searchResultsError) {
            return (
                <NoResultsNotice>
                    <p>
                        {'An error has occurred while loading this content. '}
                        <a href={window.location.pathname}>
                            {'Please try again'}
                        </a>
                    </p>
                </NoResultsNotice>
            );
        }

        if (
            (!resultsReady && results?.length === 0) ||
            (results?.length > 0 &&
                !bannerAdsHaveLoaded &&
                !bannerAdsMidSessionFetch)
        ) {
            return <SkeletonCardListV2 />;
        }

        const noResults =
            results?.length === 0 &&
            !locationSearchAPI.isLoading &&
            bannerAdsHaveLoaded &&
            bannerAds?.length === 0;

        if (noResults) {
            // No results found - show different message if the online bookings filter is on but MH1 call failed
            const text = filterByOnlineBookings ? (
                <span>
                    {
                        "Online bookings aren't available right now. Please try again later, or "
                    }
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            this.turnOffOnlineBookingsFilter();
                        }}
                    >
                        {"turn off the 'Online bookings' filter"}
                    </a>
                    {' to see more options.'}
                </span>
            ) : (
                // Default no result notification
                NoResultText
            );

            return <NotificationV2 customClass="no-result" text={text} />;
        }

        return (
            <section aria-busy={locationSearchAPI.isLoading}>
                {this.renderLoadPrevious()}
                <ul>
                    {this.renderResults()}
                    {locationSearchAPI.isLoading &&
                        !locationSearchAPI.isLoadingPrevious &&
                        results.length > 0 &&
                        Array.from({length: 2}, (_, j) => (
                            <SkeletonCardV2 key={`load-more-skeleton-${j}`} />
                        ))}
                </ul>
                {this.renderLoadMoreButton()}
            </section>
        );
    }
}